import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import {
  fetchCategories,
  deleteCategory,
  selectCategories,
  selectCategoriesLoading,
  selectCategoriesError,
  Category
} from 'store/categoriesSlice';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import PhoenixLoader from 'components/common/PhoenixLoader';
import CategoryModal from 'components/modals/CategoryModal';
import ViewCategoryModal from 'components/modals/ViewCategoryModal';
import { getCompanies, Company } from 'api/companyAPI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTag,
  faBuilding,
  faCalendar,
  faCog
} from '@fortawesome/free-solid-svg-icons';

interface CategoryTableProps {
  searchQuery?: string;
  selectedCompanyId?: number | null;
}

const CategoryTable = ({
  searchQuery = '',
  selectedCompanyId
}: CategoryTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector(selectCategories);
  const loading = useSelector(selectCategoriesLoading);
  const error = useSelector(selectCategoriesError);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [openEditModal, setOpenEditModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewCategory, setViewCategory] = useState<Category | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchCategories());
    loadCompanies();
  }, [dispatch]);

  const loadCompanies = async () => {
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setCompanies([]);
    }
  };

  const companyMap = useMemo(() => {
    const map: Record<number, string> = {};
    companies.forEach(company => {
      map[company.id] = company.name;
    });
    return map;
  }, [companies]);

  const filteredData = useMemo(() => {
    let filtered = categories;

    // Filter by company if selectedCompanyId is provided
    if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
      filtered = filtered.filter(
        category => category.company_id === selectedCompanyId
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        category =>
          category.name.toLowerCase().includes(query) ||
          category.id.toString().includes(query) ||
          (category.created_at &&
            category.created_at.toLowerCase().includes(query)) ||
          (companyMap[category.company_id] &&
            companyMap[category.company_id].toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [categories, searchQuery, selectedCompanyId, companyMap]);

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);

    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await dispatch(deleteCategory(categoryToDelete.id)).unwrap();
      setShowDeleteModal(false);
      setCategoryToDelete(null);
      dispatch(fetchCategories());
    } catch (err) {
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
          ? err.message
          : 'Failed to delete category. Please try again.';

      setDeleteError(errorMessage);

      // Display error in modal (instead of console)
      console.error('Failed to delete category:', err);
    }
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setOpenEditModal(true);
  };

  const handleView = (category: Category) => {
    setViewCategory(category);
    setOpenViewModal(true);
  };

  const table = useAdvanceTable({
    data: filteredData,
    columns: getColumns(handleView, handleEdit, handleDeleteClick, companyMap),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {loading ? (
          <PhoenixLoader />
        ) : error ? (
          <p className="p-4 text-danger">Error: {error}</p>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {openEditModal && selectedCategory && (
        <CategoryModal
          show={openEditModal}
          handleClose={() => {
            setOpenEditModal(false);
            setSelectedCategory(null);
          }}
          editingCategory={selectedCategory}
        />
      )}

      {showDeleteModal && categoryToDelete && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Category</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCategoryToDelete(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the category "
                  <strong>{categoryToDelete.name}</strong>"? This action cannot
                  be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCategoryToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openViewModal && viewCategory && (
        <ViewCategoryModal
          show={openViewModal}
          handleClose={() => {
            setOpenViewModal(false);
            setViewCategory(null);
          }}
          category={viewCategory}
          companyMap={companyMap}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default CategoryTable;

function getColumns(
  onView: (category: Category) => void,
  onEdit: (category: Category) => void,
  onDelete: (category: Category) => void,
  companyMap: Record<number, string>
): ColumnDef<Category>[] {
  return [
    {
      id: 'sn',
      header: 'SN',
      cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
      meta: {
        headerProps: {
          style: { width: '80px' }
        },
        cellProps: {}
      }
    },
    {
      id: 'name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
          Category Name
        </span>
      ),
      cell: ({ row: { original } }) => (
        <button
          className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
          onClick={() => onView(original)}
        >
          {original.name}
        </button>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'company_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
          Company
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">
          {companyMap[original.company_id] || `Company ${original.company_id}`}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'created_at',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
          Created Date
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">
          {original.created_at
            ? new Date(original.created_at).toLocaleDateString()
            : 'N/A'}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'actions',
      header: () => (
        <div className="d-inline-flex align-items-center">
          <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
          Actions
        </div>
      ),
      cell: ({ row: { original } }) => (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ActionDropdownItems
              onView={() => onView(original)}
              onEdit={() => onEdit(original)}
              onRemove={() => onDelete(original)}
            />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: {
          className: 'text-end pe-3'
        },
        cellProps: { className: 'text-end pe-3' }
      }
    }
  ];
}
